// src/app/modules/user/modules/accounts/components/patient/PatientTable.tsx
import React, {useState} from 'react'
import {Link} from 'react-router-dom'
import '@mdi/font/css/materialdesignicons.min.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import {FaHeartPulse} from 'react-icons/fa6'
import {FaUserCircle} from 'react-icons/fa'
import BloodPressureIcon from '../common/BloodPressureIcon'
import {useReduxAuth} from '../../../auth'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {RoleGuard} from '../permissions/RoleGuard'
import RequiredSubscriptionModal from '../modals/RequiredSubscriptionModal'
import {canAddPatients} from '../../helper/userHelpers'
import DataTable from 'react-data-table-component'
import {getCandiTranslations} from '../../../../helpers/translationHelper'
import {formatToUSDate, formatBillingCycleDateToUser} from '../../helper/dateTimeHelpers'
import moment from 'moment'


export interface LastReading {
  sys?: number | string
  dia?: number | string
  pulse?: number | string
  reading_taken_at?: string | null
}

export interface Patient {
  id: number
  uuid: string
  full_name: string
  dob: string | null
  gender: string
  profile_image?: string | null
  lastReading?: LastReading
  readings: number
  care_minutes: string
  status: boolean
  AssignedDoctor: {full_name: string}
  DeviceAllocations: {
    devices_de_allocation_date: string | null
    Device: {name: string; imei: string; uuid: string}
  }[]
  BillingCycle?: {
    cycle_start?: string
    cycle_end?: string
    start_date?: string
    end_date?: string
    status?: string
  } | null
  billing_cycle?: {
    cycle_start?: string
    cycle_end?: string
    start_date?: string
    end_date?: string
    status?: string
  } | null
}

export interface ActionDefault {
  name: string
  title: string
  color: string
  icon: string
  isConfirmationRequired?: boolean
}

type PatientTableDataType = {
  patients: any[]
  total: number | undefined
  trialLimit: number | null
}

interface PatientTableProps {
  data: PatientTableDataType
  showAddButton?: boolean
  onAddClick?: () => void
  actions?: ActionDefault[]
  onAction?: (a: ActionDefault, p: any) => void
  filterValues: {
    searchTerm: string
    timeSpent: string
    lastReading: string
    readingCount: string
    status: string
  }
  onFilterChange: (
    field: 'searchTerm' | 'timeSpent' | 'lastReading' | 'readingCount' | 'status',
    value: string
  ) => void
}

const timeOptions = [
  {value: '', label: 'All'},
  {value: '0-9', label: '0–9 mins'},
  {value: '10-19', label: '10–19 mins'},
  {value: '20-29', label: '20–29 mins'},
  {value: '30-39', label: '30–39 mins'},
  {value: '40-49', label: '40–49 mins'},
  {value: '50-59', label: '50–59 mins'},
  {value: '60', label: '60+ mins'},
]
const lastReadOptions = [
  {value: '', label: 'All'},
  {value: 'never', label: 'Never'},
  {value: '<24', label: 'Last 24 hours'},
  {value: '24-48', label: '24–48 hours ago'},
  {value: '48-72', label: '48–72 hours ago'},
  {value: '>72', label: 'Over 72 hours ago'},
]
const countOptions = [
  {value: '', label: 'All'},
  {value: '0-4', label: '0–4 readings'},
  {value: '5-10', label: '5–10 readings'},
  {value: '11-19', label: '11–19 readings'},
  {value: '20+', label: '20+ readings'},
]

const PatientTable: React.FC<PatientTableProps> = ({
  data,
  showAddButton = false,
  onAddClick,
  actions,
  onAction,
  filterValues,
  onFilterChange,
}) => {
  const t = getCandiTranslations()
  /* ---------- ROLE AWARENESS ---------- */
  const {currentUser} = useReduxAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const roleId = currentUser?.role_id
  const isAdmin = roleId === USER_ROLES.ADMIN || roleId === USER_ROLES.SUB_ADMIN
  const isSuperAdmin = currentUser?.role_id === USER_ROLES.ADMIN // role_id 1 = ADMIN (super admin)
  const activeTab = isAdmin ? 'overview' : 'health-metrics'

  /* ---------- HELPERS ---------- */
  // For DOB (date-only field) - use regular formatToUSDate
  const formatDOB = (d: string | null) => (d ? formatToUSDate(d) : '–')
  const calcAge = (d: string | null) => (d ? moment().diff(moment(d), 'years') : null)
  // For timestamps from database - use regular formatters
  const formatLR = (ts: string | null) => {
    if (!ts) return '–'

    const now = moment()
    const readingTime = moment(ts)
    const isFuture = readingTime.isAfter(now)

    const duration = moment.duration(isFuture ? readingTime.diff(now) : now.diff(readingTime))
    const hours = duration.asHours()
    const minutes = duration.asMinutes()

    if (isFuture) {
      const futureHours = Math.floor(hours)
      if (futureHours < 1) {
        const futureMinutes = Math.floor(minutes)
        return futureMinutes < 1
          ? 'in a few seconds'
          : `in ${futureMinutes} minute${futureMinutes !== 1 ? 's' : ''}`
      }
      return `in ${futureHours} hour${futureHours !== 1 ? 's' : ''}`
    }

    // Past timestamps
    if (hours < 1) {
      const mins = Math.floor(minutes)
      return mins < 1 ? 'a few seconds ago' : `${mins} minute${mins !== 1 ? 's' : ''} ago`
    }

    if (hours < 24) {
      const hrs = Math.floor(hours)
      return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`
    } else if (hours >= 24 && hours < 48) {
      return '24–48hr ago'
    } else if (hours >= 48 && hours < 72) {
      return '48–72hr ago'
    } else {
      return 'Over 72hr ago'
    }
  }
  const formatMTD = (m: string) => `${Math.floor(parseFloat(m))}m`
  const pickDev = (allocs: Patient['DeviceAllocations']) => {
    const a = allocs.find((x) => x.devices_de_allocation_date === null)
    return a ? a.Device?.name : 'Unassigned'
  }

  // Helper function to render billing cycle status
  const renderBillingCycleStatus = (p: any) => {
    // First try to get the active billing cycle from BillingCycles array
    let billingCycle = p?.BillingCycles?.[0] || null

    // For active cycles, use the dates as-is without alignment
    let startDate = formatBillingCycleDateToUser(billingCycle?.cycle_start)
    let endDate = formatBillingCycleDateToUser(billingCycle?.cycle_end)

    const dateRange = billingCycle ? `${startDate} - ${endDate}` : `-`

    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'}}>
        <span>{dateRange}</span>
      </div>
    )
  }

  // Helper function to render actions dropdown
  const renderActions = (p: any) => {
    return (
      <RoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
        <div className='dropdown'>
          <button
            className='btn btn-sm'
            type='button'
            data-bs-toggle='dropdown'
            aria-expanded='false'
          >
            <i className='mdi mdi-dots-horizontal'></i>
          </button>
          <ul className='dropdown-menu'>
            <li>
              {!p.status ? (
                <button
                  className='dropdown-item'
                  onClick={() => {
                    canAddPatients(currentUser)
                      ? onAction?.(
                          {
                            name: 'CHANGE_STATUS',
                            title: 'Activate',
                            color: 'green',
                            icon: 'mdi-check',
                          },
                          p
                        )
                      : setIsModalOpen(true)
                  }}
                >
                  Activate
                </button>
              ) : (
                <button
                  className='dropdown-item'
                  onClick={() => {
                    onAction?.(
                      {
                        name: 'CHANGE_STATUS',
                        title: 'Deactivate',
                        color: 'red',
                        icon: 'mdi-close',
                      },
                      p
                    )
                  }}
                >
                  Deactivate
                </button>
              )}
            </li>
          </ul>
        </div>
      </RoleGuard>
    )
  }

  // Define columns for admin users
  const adminColumns = [
    {
      name: '#',
      cell: (row: any, index: number) => index + 1,
      width: '70px',
      sortable: false,
    },
    {
      name: 'Name',
      cell: (row: any) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          <FaUserCircle size={32} className='text-muted' />
          <div>
            {isSuperAdmin ? (
              <h6 className='fw-semibold mb-0'>{row.full_name}</h6>
            ) : (
              <Link
                to={`/patient-details/${row.uuid}/${activeTab}`}
                className='fw-semibold d-block'
              >
                {row.full_name}
              </Link>
            )}
            {calcAge(row.dob) != null && (
              <div className='small text-muted'>
                {calcAge(row.dob)} yrs • {row.gender[0].toUpperCase() + row.gender.slice(1)}
              </div>
            )}
          </div>
        </div>
      ),
      selector: (row: any) => row.full_name,
      sortable: true,
      minWidth: '200px',
      grow: 1,
    },
    {
      name: 'Doctor',
      cell: (row: any) => row.AssignedDoctor.full_name,
      selector: (row: any) => row.AssignedDoctor.full_name,
      sortable: true,
      minWidth: '150px',
    },
    {
      name: 'Devices',
      cell: (row: any) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          <div style={{width: '36px'}}>
            <BloodPressureIcon
              width={36}
              height={36}
              fillColor={pickDev(row.DeviceAllocations) === 'Unassigned' ? '#555' : '#1DCFBE'}
            />
          </div>
          <div>
            <p className='fw-bold mb-0 small'>BP</p>
            <div className='small'>{pickDev(row.DeviceAllocations)}</div>
          </div>
        </div>
      ),
      selector: (row: any) => pickDev(row.DeviceAllocations),
      sortable: true,
      minWidth: '180px',
    },
    // {
    //   name: 'Last Reading',
    //   cell: (row: any) => <span className=''>{formatLR(row.lastReading?.reading_taken_at)}</span>,
    //   selector: (row: any) => row.lastReading?.reading_taken_at,
    //   sortable: true,
    //   minWidth: '120px',
    // },
    {
      name: t.readings,
      cell: (row: any) => <span className=''>{row.readings}</span>,
      selector: (row: any) => row.readings,
      sortable: true,
      width: '100px',
      center: true,
    },
    {
      name: t.careMins,
      cell: (row: any) => <span className=''>{formatMTD(row.care_minutes)}</span>,
      selector: (row: any) => parseFloat(row.care_minutes),
      sortable: true,
      width: '100px',
      center: true,
    },
    {
      name: 'Billing Cycle',
      cell: (row: any) => renderBillingCycleStatus(row),
      selector: (row: any) => {
        // Use same logic as renderBillingCycleStatus for consistency
        let billingCycle = null
        if (row?.BillingCycles?.length > 0) {
          billingCycle =
            row.BillingCycles.find((cycle: any) => cycle.status === 'active') ||
            row.BillingCycles[row.BillingCycles.length - 1]
        }
        const startDateValue =
          billingCycle?.cycle_start ?? billingCycle?.start_date ?? billingCycle?.start
        if (!startDateValue) return 0
        return moment(startDateValue).valueOf()
      },
      sortable: true,
      minWidth: '140px',
      center: true,
    },
  ]

  // Define columns for doctor/nurse users
  const doctorColumns = [
    {
      name: '#',
      cell: (row: any, index: number) => index + 1,
      width: '60px',
      sortable: false,
    },
    {
      name: 'Patient',
      cell: (row: any) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          <FaUserCircle size={28} className='text-muted' />
          <div>
            {isSuperAdmin ? (
              <h6 className='fw-semibold mb-0' style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '140px',
              }}>{row.full_name}</h6>
            ) : (
                <Link
                  to={`/patient-details/${row.uuid}/${activeTab}`}
                  className='fw-semibold d-block' style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '140px',
                  }}>
                  {row.full_name}
                </Link>
            )}
            {calcAge(row.dob) != null && (
              <div className='small text-muted'>
                {calcAge(row.dob)} yrs • {row.gender[0].toUpperCase() + row.gender.slice(1)}
              </div>
            )}
          </div>
        </div>
      ),
      selector: (row: any) => row.full_name,
      sortable: true,
      minWidth: '180px',
      grow: 1,
    },
    {
      name: 'D.O.B.',
      cell: (row: any) => formatDOB(row.dob),
      selector: (row: any) => row.dob,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Latest Reading',
      cell: (row: any) => {
        const {sys, dia, pulse} = row.lastReading ?? {}
        const hasVitals = sys != null && dia != null && pulse != null

        return hasVitals ? (
          <div style={{display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px'}}>
            <div className='d-flex align-items-center gap-1'>
              <BloodPressureIcon width={20} height={20} fillColor='#333333' />
              <span className='small'>
                {sys}/{dia} mmHg
              </span>
            </div>
            <div className='d-flex align-items-center gap-1'>
              <FaHeartPulse size={18} />
              <span className='small'>{pulse} bpm</span>
            </div>
          </div>
        ) : (
          '–'
        )
      },
      selector: (row: any) => row.lastReading?.sys ?? 0,
      sortable: true,
      minWidth: '160px',
      maxWidth: '200px',
    },
    {
      name: 'Devices',
      cell: (row: any) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          <div style={{width: '36px'}}>
            <BloodPressureIcon
              width={32}
              height={32}
              fillColor={pickDev(row.DeviceAllocations) === 'Unassigned' ? '#555' : '#1DCFBE'}
            />
          </div>
          <div>
            <p className='fw-bold mb-0 small'>BP</p>
            <div className='small'>{pickDev(row.DeviceAllocations)}</div>
          </div>
        </div>
      ),
      selector: (row: any) => pickDev(row.DeviceAllocations),
      sortable: true,
      minWidth: '140px',
    },
    {
      name: 'Last Reading',
      cell: (row: any) => <span className=''>{formatLR(row.lastReading?.reading_taken_at)}</span>,
      selector: (row: any) => row.lastReading ? new Date(row.lastReading?.reading_taken_at).getTime() : 0,
      sortable: true,
      minWidth: '120px',
    },
    {
      name: t.readings,
      cell: (row: any) => <span className=''>{row.readings}</span>,
      selector: (row: any) => row.readings,
      sortable: true,
      width: '100px',
      center: true,
    },
    {
      name: t.careMins,
      cell: (row: any) => <span className=''>{formatMTD(row.care_minutes)}</span>,
      selector: (row: any) => parseFloat(row.care_minutes),
      sortable: true,
      width: '100px',
      center: true,
    },
    {
      name: 'Billing Cycle',
      cell: (row: any) => renderBillingCycleStatus(row),
      selector: (row: any) => {
        // Use same logic as renderBillingCycleStatus for consistency
        let billingCycle = null
        if (row?.BillingCycles?.length > 0) {
          // Find active billing cycle first, then fall back to the most recent one
          billingCycle =
            row.BillingCycles.find((cycle: any) => cycle.status === 'active') ||
            row.BillingCycles[row.BillingCycles.length - 1]
        }

        // For active cycles, use the dates as-is without alignment
        const startDateValue =
          billingCycle?.cycle_start ?? billingCycle?.start_date ?? billingCycle?.start
        if (!startDateValue) return 0
        return moment(startDateValue).valueOf()
      },
      sortable: true,
      minWidth: '140px',
      center: true,
    },
    {
      name: '', //! INFO: actions column name is removed intentionally
      cell: (row: any) => renderActions(row),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '80px',
      center: true,
    },
  ]

  const columns = isAdmin ? adminColumns : doctorColumns

  const isTrialUser = currentUser.subscription_status === 'trial'

  const patientCounts = 
    data.total !== undefined && data.trialLimit !== undefined
      ? `(${data.total} / ${data.trialLimit})`
      : ''

  const isTrialLimitReached =isTrialUser && data.trialLimit && data.total && data.total >= data.trialLimit

  return (
    <>
      <div className='bg-white p-4 rounded-4 shadow-sm'>
        <div className='d-xxl-flex d-block justify-content-between align-items-start mb-4'>
          <PatientTableHeader
            showAddButton={showAddButton}
            onAddClick={onAddClick}
            isTrialUser={isTrialUser}
            data={data}
          />
          <RoleGuard allowedRoles={[USER_ROLES.NURSE, USER_ROLES.DOCTOR, USER_ROLES.MEDICAL_ASSISTANT]}>
            <div className='d-flex  flex-column flex-md-row flex-wrap align-items-start align-items-md-end  g-3 mt-xl-0 mt-2'>
              {/* — Name search — */}
              <div className='me-3'>
                <label className='form-label mb-1'>Search</label>
                <input
                  type='text'
                  placeholder='Search by name'
                  className='form-control'
                  value={filterValues.searchTerm}
                  onChange={(e) => onFilterChange('searchTerm', e.target.value)}
                />
              </div>

              <div className='d-flex align-items-end gap-3 mt-2'>
                <div>
                  <label className='form-label mb-1 d-flex align-items-center gap-2'>Status</label>
                  <select
                    className='form-select border-primary'
                    value={filterValues.status}
                    onChange={(e) => onFilterChange('status', e.target.value)}
                  >
                    <option value='all'>All</option>
                    <option value='active'>Active</option>
                    <option value='inactive'>Inactive</option>
                  </select>
                </div>

                <div>
                  <label className='form-label mb-1 d-flex align-items-center gap-2'>
                    Last Reading
                  </label>
                  <select
                    className={`form-select ${filterValues.lastReading ? 'border-primary' : ''}`}
                    value={filterValues.lastReading}
                    onChange={(e) => onFilterChange('lastReading', e.target.value)}
                  >
                    {lastReadOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className='form-label mb-1 d-flex align-items-center gap-2'>
                    Care Mins
                  </label>
                  <select
                    className={`form-select ${filterValues.timeSpent ? 'border-primary' : ''}`}
                    value={filterValues.timeSpent}
                    onChange={(e) => onFilterChange('timeSpent', e.target.value)}
                  >
                    {timeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className='form-label mb-1 d-flex align-items-center gap-2'>
                    Reading Count
                  </label>
                  <select
                    className={`form-select ${filterValues.readingCount ? 'border-primary' : ''}`}
                    value={filterValues.readingCount}
                    onChange={(e) => onFilterChange('readingCount', e.target.value)}
                  >
                    {countOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='d-xxl-flex d-none ms-2'>
                {showAddButton && onAddClick && (
                  <button
                    title={isTrialLimitReached ? 'Patient Limit reached' : ''}
                    className={`btn text-nowrap py-2  ${isTrialLimitReached
                        ? 'btn-secondary text-gray cursor-disabled'
                        : 'btn-primary text-white'
                      }`}
                    onClick={onAddClick}
                  >
                    Add Patient {isTrialUser && patientCounts && patientCounts}
                  </button>
                )}
              </div>
            </div>
          </RoleGuard>
        </div>
        <RoleGuard allowedRoles={[USER_ROLES.NURSE, USER_ROLES.DOCTOR, USER_ROLES.MEDICAL_ASSISTANT]}>
          {(filterValues.timeSpent || filterValues.lastReading || filterValues.readingCount) && (
            <div className='mt-3 p-2 bg-light rounded-3 border-start border-primary border-4'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='text-primary fw-bold'>
                  <i className='mdi mdi-filter-variant me-1'></i> Active Filters
                </span>
              </div>
              <div className='d-flex gap-2 flex-wrap'>
                {filterValues.timeSpent && (
                  <div className='badge bg-white text-dark border px-3 py-2 d-flex align-items-center'>
                    <span className='text-primary me-1'>
                      <i className='mdi mdi-clock-outline'></i>
                    </span>
                    <span>
                      Time: {timeOptions.find((o) => o.value === filterValues.timeSpent)?.label}
                    </span>
                    <button
                      type='button'
                      className='btn-close ms-2'
                      style={{fontSize: '0.65rem'}}
                      onClick={() => onFilterChange('timeSpent', '')}
                    ></button>
                  </div>
                )}
                {filterValues.lastReading && (
                  <div className='badge bg-white text-dark border px-3 py-2 d-flex align-items-center'>
                    <span className='text-primary me-1'>
                      <i className='mdi mdi-history'></i>
                    </span>
                    <span>
                      Reading:{' '}
                      {lastReadOptions.find((o) => o.value === filterValues.lastReading)?.label}
                    </span>
                    <button
                      type='button'
                      className='btn-close ms-2'
                      style={{fontSize: '0.65rem'}}
                      onClick={() => onFilterChange('lastReading', '')}
                    ></button>
                  </div>
                )}
                {filterValues.readingCount && (
                  <div className='badge bg-white text-dark border px-3 py-2 d-flex align-items-center'>
                    <span className='text-primary me-1'>
                      <i className='mdi mdi-counter'></i>
                    </span>
                    <span>
                      Reads:{' '}
                      {countOptions.find((o) => o.value === filterValues.readingCount)?.label}
                    </span>
                    <button
                      type='button'
                      className='btn-close ms-2'
                      style={{fontSize: '0.65rem'}}
                      onClick={() => onFilterChange('readingCount', '')}
                    ></button>
                  </div>
                )}
              </div>
            </div>
          )}
        </RoleGuard>

        {/* ---------- DATA TABLE ---------- */}
        <div className='table-responsive'>
          <DataTable
            columns={columns}
            data={data.patients}
            pagination
            responsive
            highlightOnHover
            dense
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 30]}
            noDataComponent={<div className='py-4 text-center text-muted'>No patients found</div>}
            customStyles={{
              table: {
                style: {
                  minWidth: '1200px',
                },
              },
              headCells: {
                style: {
                  color: '#333',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  fontSize: '13px',
                },
              },
              rows: {
                style: {
                  backgroundColor: '#FFFFFF',
                  borderBottom: '1px solid #e9ecef',
                  minHeight: '80px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                },
              },
              pagination: {
                style: {
                  borderTop: '1px solid #dee2e6',
                  minHeight: '56px',
                },
              },
            }}
          />
        </div>
      </div>

      <RequiredSubscriptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(!isModalOpen)}
      />
    </>
  )
}

interface PatientTableHeaderProps {
  showAddButton?: boolean
  isTrialUser: boolean
  data: PatientTableDataType
  onAddClick?: () => void
}

const PatientTableHeader: React.FC<PatientTableHeaderProps> = ({
  showAddButton,
  onAddClick,
  isTrialUser,
  data,
}) => {
  const isTrialLimitReached = data.trialLimit && data.total && data.total >= data.trialLimit

  return (
    <div className='d-flex justify-content-between align-items-center'>
      <div className=''>
        <h1>Patients List</h1>
        <div className='fs-6'></div>
      </div>  
      <div className='d-xxl-none d-flex justify-content-end'>
        {showAddButton && onAddClick && (
          <button
            title={isTrialLimitReached ? 'Patient Limit reached' : ''}
            className={`btn text-nowrap py-2  ${
              (isTrialUser && isTrialLimitReached)
                ? 'btn-secondary text-gray cursor-disabled'
                : 'btn-primary text-white'
            }`}
            onClick={onAddClick}
          >
            Add Patient
          </button>
        )}
      </div>
    </div>
  )
}

export default PatientTable
